import React, { useState, useEffect, useCallback } from 'react';
import { getClientEnv } from '@/config/clientEnv';
import { cn } from '@/lib/utils';
import { Home, Building, Building2, MapPin } from 'lucide-react';
import { Skeleton } from './ui/skeleton';

interface GridFSImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  fallbackSrc?: string;
  propertyType?: string;
  priority?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'featured' | 'medium';
  isFeatured?: boolean;
}

export function GridFSImage({ 
  src, 
  fallbackSrc, 
  propertyType,
  priority = false,
  size = 'md',
  isFeatured = false,
  alt = 'Propiedad',
  className = '',
  ...props 
}: GridFSImageProps) {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [showImage, setShowImage] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [retryCount, setRetryCount] = useState<number>(0);
  const [imageKey, setImageKey] = useState<string>(`img-${Date.now()}`);
  const [apiBaseUrl, setApiBaseUrl] = useState<string>('');
  const [serverPort, setServerPort] = useState<string>('3000');
  const [errorMessage, setErrorMessage] = useState<string>('');
  
  // Función para registrar mensajes de depuración solo en desarrollo
  const logDebug = (message: string) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(message);
    }
  };
  
  // Obtener la URL base de la API
  useEffect(() => {
    const env = getClientEnv();
    const baseUrl = env.apiUrl || window.location.origin;
    setApiBaseUrl(baseUrl);
    
    // Intentar obtener el puerto del servidor para desarrollo local
    fetch(`${baseUrl}/api/health`)
      .then(response => {
        const port = response.headers.get('X-Server-Port');
        if (port) {
          setServerPort(port);
          logDebug(`[GridFSImage] Puerto del servidor actualizado: ${port}`);
        }
        return response.json();
      })
      .catch(err => {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[GridFSImage] No se pudo verificar el puerto del servidor:', err);
        }
      });
  }, []);

  // Procesar la URL de la imagen
  const processImageUrl = useCallback(() => {
    if (!src || src === 'undefined' || src === 'null' || src === '') {
      logDebug('[GridFSImage] No se proporcionó una URL de imagen válida');
      setErrorMessage('URL de imagen no válida');
      setShowImage(false);
      setIsLoading(false);
      return;
    }

    // Limpiar cualquier comilla en la URL
    let cleanSrc = src.replace(/"/g, '');
    
    // Corregir URLs duplicadas de la API (ej: /api/images//api/images/...)
    if (cleanSrc.includes('/api/images/')) {
      const parts = cleanSrc.split('/api/images/');
      const id = parts[parts.length - 1]; // Tomar el último ID
      cleanSrc = `/api/images/${id}`;
    }
    
    logDebug(`[GridFSImage] Processing image URL: ${cleanSrc}`);
    
    // Prioridad 1: URL completa (incluye http o https)
    if (cleanSrc.startsWith('http://') || cleanSrc.startsWith('https://')) {
      logDebug(`[GridFSImage] Using direct URL: ${cleanSrc}`);
      setImageSrc(cleanSrc);
      setIsLoading(true);
      return;
    }

    // Verificar si es una URL de imagen válida
    const isApiImage = cleanSrc.includes('/api/images/');
    const isUploadImage = cleanSrc.includes('/uploads/');
    const isMongoId = cleanSrc.match(/^[0-9a-fA-F]{24}$/);
    
    // Prioridad 2: URL de API (/api/images/) pero sin prefijo
    if (isApiImage) {
      // Si ya es una ruta relativa que empieza por /api/images/
      // o una ruta que contiene /api/images/ pero sin ser URL completa (ya manejado arriba)
      const fullUrl = cleanSrc.startsWith('/') 
        ? `${apiBaseUrl}${cleanSrc}` 
        : `${apiBaseUrl}/${cleanSrc}`;
      logDebug(`[GridFSImage] Using API images URL: ${fullUrl}`);
      setImageSrc(fullUrl);
      setIsLoading(true);
      return;
    }
    
    // Prioridad 3: ID de MongoDB sin ruta (24 caracteres hexadecimales)
    if (isMongoId) {
      const mongoUrl = `${apiBaseUrl}/api/images/${cleanSrc}`;
      logDebug(`[GridFSImage] Using MongoDB ID: ${mongoUrl}`);
      setImageSrc(mongoUrl);
      setIsLoading(true);
      return;
    }
    
    // Prioridad 4: Ruta que comienza con /uploads/ - URL relativa al servidor
    if (isUploadImage) {
      // Intentar convertir la ruta de uploads a una ruta de API
      // Esto es útil si la imagen se subió originalmente a través de GridFS
      const fileId = cleanSrc.split('/').pop();
      if (fileId && fileId.match(/^[0-9a-fA-F]{24}$/)) {
        const apiImageUrl = `${apiBaseUrl}/api/images/${fileId}`;
        logDebug(`[GridFSImage] Converting uploads URL to API URL: ${apiImageUrl}`);
        setImageSrc(apiImageUrl);
        setIsLoading(true);
        return;
      }
      
      // Si no es un ID de MongoDB, usar la ruta de uploads directamente
      const fullUrl = cleanSrc.startsWith('/') 
        ? `${apiBaseUrl}${cleanSrc}` 
        : `${apiBaseUrl}/${cleanSrc}`;
      logDebug(`[GridFSImage] Using uploads URL: ${fullUrl}`);
      setImageSrc(fullUrl);
      setIsLoading(true);
      return;
    }
    
    // Prioridad 5: Cualquier otra ruta relativa
    const defaultUrl = cleanSrc.startsWith('/') 
      ? `${apiBaseUrl}${cleanSrc}` 
      : `${apiBaseUrl}/${cleanSrc}`;
    logDebug(`[GridFSImage] Using default URL construction: ${defaultUrl}`);
    setImageSrc(defaultUrl);
    setIsLoading(true);
  }, [src, apiBaseUrl]);

  // Manejar errores de carga de imagen
  const handleError = useCallback(() => {
    logDebug(`[GridFSImage] Error loading image: ${imageSrc}, retry count: ${retryCount}`);
    
    if (retryCount >= 3) {
      setErrorMessage(`No se pudo cargar la imagen después de ${retryCount} intentos`);
      
      // Si hay un fallback específico, usarlo
      if (fallbackSrc) {
        setImageSrc(fallbackSrc);
        setImageKey(`fallback-${Date.now()}`);
        setRetryCount(0);
        return;
      }
      
      // Si la URL original es un ID de MongoDB, intentar con la URL completa
      const isMongoId = src.match(/^[0-9a-fA-F]{24}$/);
      if (isMongoId) {
        const fullUrl = `${apiBaseUrl}/api/images/${src}`;
        setImageSrc(fullUrl);
        setImageKey(`mongo-${Date.now()}`);
        setRetryCount(0);
        return;
      }
      
      // Si la URL original es una ruta de uploads, intentar con la URL completa
      const isUploadPath = src.includes('/uploads/');
      if (isUploadPath) {
        const fullUrl = src.startsWith('/') 
          ? `${apiBaseUrl}${src}` 
          : `${apiBaseUrl}/${src}`;
        setImageSrc(fullUrl);
        setImageKey(`upload-${Date.now()}`);
        setRetryCount(0);
        return;
      }
      
      // Si todo lo demás falla, mostrar el placeholder
      setShowImage(false);
      setIsLoading(false);
      return;
    }
    
    // Incrementar el contador de reintentos
    setRetryCount(prev => prev + 1);
    
    // Estrategia de reintento basada en el tipo de URL
    if (retryCount === 0) {
      // Primer reintento: intentar con la URL de API
      const apiUrl = `/api/images/${src.split('/').pop()}`;
      logDebug(`[GridFSImage] Retrying with API URL: ${apiUrl}`);
      setImageSrc(apiUrl);
      setImageKey(`retry1-${Date.now()}`);
    } else if (retryCount === 1) {
      // Segundo reintento: intentar con la URL de uploads
      const uploadsUrl = `/uploads/${src.split('/').pop()}`;
      logDebug(`[GridFSImage] Retrying with uploads URL: ${uploadsUrl}`);
      setImageSrc(uploadsUrl);
      setImageKey(`retry2-${Date.now()}`);
    } else {
      // Tercer reintento: volver a la URL original
      logDebug(`[GridFSImage] Retrying with original URL: ${imageSrc}`);
      setImageSrc(src);
      setImageKey(`retry3-${Date.now()}`);
    }
    
    // Si después de 3 reintentos sigue fallando, mostrar un mensaje
    if (retryCount === 2) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[GridFSImage] Error loading image after ${retryCount} retries: ${imageSrc}. Checking for fallback.`);
      }
      
      // Si hay un fallback, usarlo
      if (fallbackSrc) {
        logDebug(`[GridFSImage] Using fallback image: ${fallbackSrc}`);
        setImageSrc(fallbackSrc);
        setImageKey(`fallback-final-${Date.now()}`);
        setRetryCount(0);
      } else {
        // Si no hay fallback, mostrar el placeholder
        setShowImage(false);
        setIsLoading(false);
      }
    }
  }, [imageSrc, retryCount, src, apiBaseUrl, fallbackSrc]);

  // Resetear el estado cuando cambia la URL de la imagen
  useEffect(() => {
    setRetryCount(0);
    setIsLoading(true);
    setShowImage(true);
    setErrorMessage('');
    setImageKey(`src-${src}-${Date.now()}`);
  }, [src]);

  // Procesar la URL cuando cambia la fuente o la URL de la API
  useEffect(() => {
    if (apiBaseUrl) {
      processImageUrl();
    }
  }, [src, apiBaseUrl, processImageUrl]);

  // Manejar la carga exitosa de la imagen
  const handleLoad = () => {
    setIsLoading(false);
    logDebug(`[GridFSImage] Successfully loaded image: ${imageSrc}`);
  };

  // Determinar el tamaño de la imagen
  const getSizeClasses = () => {
    switch (size) {
      case 'sm': return 'h-24 w-24 object-cover';
      case 'md': return 'h-48 w-48 object-cover';
      case 'lg': return 'h-64 w-64 object-cover';
      case 'xl': return 'w-full h-full object-cover';
      case 'full': return 'w-full h-full object-cover';
      // Compatibilidad con tamaños antiguos
      case 'small' as any: return 'h-24 w-24 object-cover';
      case 'medium' as any: return 'h-48 w-48 object-cover';
      case 'large' as any: return 'h-64 w-64 object-cover';
      case 'featured' as any: return 'w-full h-full object-cover';
      default: return 'h-48 w-48 object-cover';
    }
  };

  // Obtener las clases para la imagen
  const getImageClasses = () => {
    return cn(
      "absolute inset-0 w-full h-full transition-opacity duration-300",
      getSizeClasses(),
      isLoading ? "opacity-0" : "opacity-100",
      className
    );
  };

  // Renderizar un placeholder basado en el tipo de propiedad
  const renderPlaceholder = () => {
    let Icon = Home;
    
    if (propertyType) {
      switch (propertyType.toLowerCase()) {
        case 'apartamento':
        case 'departamento':
          Icon = Building;
          break;
        case 'oficina':
        case 'local':
          Icon = Building2;
          break;
        case 'terreno':
          Icon = MapPin;
          break;
        default:
          Icon = Home;
      }
    }
    
    return (
      <div className="absolute inset-0 w-full h-full bg-gray-100 flex items-center justify-center">
        <div className="text-center p-4">
          <Icon className="mx-auto h-12 w-12 text-gray-400 mb-2" />
          <p className="text-gray-500 text-sm">Imagen no disponible</p>
          {errorMessage && process.env.NODE_ENV === 'development' && (
            <p className="text-xs text-red-400 mt-1">{errorMessage}</p>
          )}
        </div>
      </div>
    );
  };

  // Si no hay imagen para mostrar, mostrar un placeholder
  if (!showImage || !imageSrc) {
    return renderPlaceholder();
  }

  return (
    <>
      {isLoading && renderPlaceholder()}
      <img
        key={imageKey}
        src={imageSrc}
        alt={alt}
        onError={handleError}
        onLoad={handleLoad}
        className={getImageClasses()}
        loading={priority ? "eager" : "lazy"}
        {...props}
      />
    </>
  );
} 